import React from 'react';
import { motion } from 'framer-motion';
import { Bot, PhoneCall, Star, Send, ArrowRight } from 'lucide-react';

const USE_CASES = [
  {
    icon: Send,
    label: 'Lead generation',
    title: 'An AI agent that finds and emails your next 500 customers',
    problem: 'No time to prospect',
    outcome: '~480 personalized outreach emails per day — fully automated',
    bullets: [
      'Discovers businesses in your target market by city and industry',
      'Scrapes contact info, pulls Google reviews, writes a personalized opener',
      'Sends, tracks replies, and follows up — all without you touching it',
    ],
  },
  {
    icon: PhoneCall,
    label: 'AI receptionist',
    title: 'Never lose a job to voicemail again',
    problem: 'Missed calls = lost revenue',
    outcome: 'Instant SMS response + 24/7 chatbot handles FAQs and routes hot leads',
    bullets: [
      'Missed call triggers an immediate text-back within seconds',
      'On-site chatbot answers hours, pricing, and availability questions',
      'Urgent leads get routed straight to your phone in real time',
    ],
  },
  {
    icon: Star,
    label: 'Reputation management',
    title: 'Every review replied to — in your voice, in 30 seconds',
    problem: 'Reviews piling up unanswered',
    outcome: 'AI drafts on-brand replies you approve with one click',
    bullets: [
      'Monitors Google, Yelp, and Facebook for new reviews',
      'Drafts a response that sounds like you — not a bot',
      'Flags negative reviews immediately so you can respond fast',
    ],
  },
  {
    icon: Bot,
    label: 'Custom AI agents',
    title: 'A custom AI built around how your business actually works',
    problem: 'Generic tools that don\'t fit',
    outcome: 'Purpose-built agent for your specific workflow — from scoping to production',
    bullets: [
      'Document processing, scheduling, quoting, customer follow-up',
      'Integrates with your existing CRM, calendar, or inbox',
      'You own the code — no vendor lock-in',
    ],
  },
];

function UseCaseCard({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px 200px 0px' }}
      transition={{ duration: 0.3, delay: index * 0.06 }}
      className="group relative glass rounded-2xl p-7 hover:border-accent/40 transition-colors h-full"
    >
      <div className="absolute -inset-px rounded-2xl bg-gradient-brand opacity-0 group-hover:opacity-10 transition-opacity pointer-events-none" />
      <div className="relative">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-gradient-brand-soft flex items-center justify-center border border-border flex-shrink-0">
            <item.icon size={20} className="text-accent-2" />
          </div>
          <span className="text-xs text-accent-2 font-medium uppercase tracking-wider">{item.label}</span>
        </div>
        <h3 className="font-display font-bold text-xl text-text mb-3 leading-snug">{item.title}</h3>
        <div className="flex items-start gap-2 mb-4 p-3 rounded-xl bg-surface-2 border border-border">
          <span className="text-xs text-text-muted leading-relaxed">
            <span className="text-text font-medium">Result: </span>{item.outcome}
          </span>
        </div>
        <ul className="space-y-2">
          {item.bullets.map((b) => (
            <li key={b} className="text-sm text-text-muted flex items-start gap-2">
              <span className="mt-1.5 w-1 h-1 rounded-full bg-accent-2 flex-shrink-0" />
              {b}
            </li>
          ))}
        </ul>
      </div>
    </motion.div>
  );
}

function FeaturedProjects() {
  return (
    <section id="ai" className="relative py-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="text-sm text-accent-2 font-medium mb-3 uppercase tracking-wider">AI in practice</p>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-text mb-4">
              What AI actually does for small businesses.
            </h2>
            <p className="text-text-muted text-lg">
              Not theory. These are real systems I build and run — automating the work that eats your day so you can focus on the work that grows your business.
            </p>
          </div>
          <a
            href="#contact"
            className="group inline-flex items-center gap-2 text-sm font-medium gradient-text whitespace-nowrap flex-shrink-0"
          >
            Talk about your business
            <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {USE_CASES.map((item, i) => (
            <UseCaseCard key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default FeaturedProjects;
